import React from 'react';

import { useIntl } from 'react-intl';

import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useSelectedProduct } from '../../../../../controllers/store/search';
import { filter as folderFilter } from '../../../../../controllers/store/search/filters/folder';
import { filter as spaceFilter } from '../../../../../controllers/store/search/filters/space';

import { i18n } from './messages';

const FolderFilter = ({
	isFilterInDropdown,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: import('../../../../../common/ui/filters/product-filters/types').FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [
		filterData,
		{
			loadInitialOptions,
			setLookupInput,
			setOption: setOptionForSelectFilter,
			clearLookupInput,
			clearOptions,
		},
	] = useSelectFilter({ filterId: folderFilter.id });
	const [spaceFilterData] = useSelectFilter({ filterId: spaceFilter.id });

	if (
		!fg('loom-rovo-search') ||
		selectedProduct !== ProductKeys.Loom ||
		!filterData ||
		!spaceFilterData?.selectedOptions.length
	) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;
	const groupedOptions = [
		{ label: formatMessage(i18n.selected), options: selectedOptions },
		{ label: formatMessage(i18n.suggested), options: suggestedOptions },
	];
	const label = fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.folder);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option: SelectFilterOption | SelectFilterOption[]) => {
				setOptionForSelectFilter(option, undefined, ProductKeys.Loom);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.placeholder)}
			filterOrControlId={folderFilter.id}
			testId="search-dialog-loom-folder-filter"
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: folderFilter.id });
			}}
			iconBefore={<FolderOpenIcon label={formatMessage(i18n.folderIcon)} />}
			isFilterInDropdown={isFilterInDropdown}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default FolderFilter;
